import { useEffect, useMemo, useRef, useState } from "react";
import { createMapBaseLayers } from "../trips/mapFoundation";
import { useI18n } from "../i18n/I18nProvider";

type MapPoint = { id: string; name: string; latitude: number; longitude: number; number?: number };
const leafletAddress = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const leafletCss = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
type MapWindow = Window & { L?: any; __mundelyPoiLeaflet?: Promise<any> };
function loadLeaflet(): Promise<any> {
  const target = window as MapWindow;
  if (target.L?.map) return Promise.resolve(target.L);
  if (target.__mundelyPoiLeaflet) return target.__mundelyPoiLeaflet;
  target.__mundelyPoiLeaflet = new Promise((resolve,reject) => {
    if (!document.querySelector('link[data-mundely-poi-leaflet]')) {
      const link=document.createElement('link');link.rel='stylesheet';link.href=leafletCss;link.dataset.mundelyPoiLeaflet='true';document.head.appendChild(link);
    }
    const script=document.createElement('script');script.src=leafletAddress;script.async=true;
    const timeout=window.setTimeout(()=>reject(new Error('map-timeout')),15000);
    script.onload=()=>{window.clearTimeout(timeout);target.L?.map?resolve(target.L):reject(new Error('map-unavailable'));};
    script.onerror=()=>{window.clearTimeout(timeout);reject(new Error('map-unavailable'));};
    document.head.appendChild(script);
  }).catch(error=>{target.__mundelyPoiLeaflet=undefined;throw error;});
  return target.__mundelyPoiLeaflet;
}
/** Fallback for devices without Google Maps: geographical positions are never shifted. */
export function PlaceResultsMap({label,center,zoom=12,points,onSelect,showResultButtons=true}:{label:string;center:{latitude:number;longitude:number}|null;zoom?:number;points:MapPoint[];onSelect:(id:string)=>void;showResultButtons?:boolean}) {
  const {language}=useI18n();
  const node=useRef<HTMLDivElement|null>(null);
  const map=useRef<any>(null);
  const frame=useRef("");
  const [ready,setReady]=useState(false);
  const [error,setError]=useState(false);
  const valid=useMemo(()=>points.filter(p=>Number.isFinite(p.latitude)&&Number.isFinite(p.longitude)&&Math.abs(p.latitude)<=90&&Math.abs(p.longitude)<=180),[points]);
  useEffect(()=>{
    let disposed=false,observer:ResizeObserver|undefined;
    void loadLeaflet().then(L=>{
      if(disposed||!node.current)return;
      const instance=L.map(node.current,{scrollWheelZoom:false,worldCopyJump:true});
      if(center&&Number.isFinite(center.latitude)&&Number.isFinite(center.longitude))instance.setView([center.latitude,center.longitude],zoom);
      else instance.fitBounds([[-60,-180],[80,180]],{animate:false});
      map.current=instance;
      createMapBaseLayers(L).classic.addTo(instance);
      observer=new ResizeObserver(()=>instance.invalidateSize());observer.observe(node.current);
      setReady(true);
    }).catch(()=>{if(!disposed)setError(true);});
    return()=>{disposed=true;observer?.disconnect();map.current?.remove();map.current=null;frame.current="";};
  },[]);
  useEffect(()=>{
    if(!ready||!map.current)return;
    const L=(window as MapWindow).L;if(!L)return;
    const group=L.layerGroup().addTo(map.current);
    for(const point of valid){
      const icon=L.divIcon({className:'mundely-poi-numbered-marker',html:`<span>${point.number??'•'}</span>`,iconSize:[36,36],iconAnchor:[18,34]});
      const marker=L.marker([point.latitude,point.longitude],{icon,title:point.name}).addTo(group);
      marker.on('click',()=>onSelect(point.id));
      const tooltip=document.createElement("span");tooltip.textContent=point.name;marker.bindTooltip(tooltip);
    }
    const key=JSON.stringify([valid.map(p=>[p.latitude,p.longitude]),center?.latitude,center?.longitude,zoom]);
    if(frame.current!==key){
      frame.current=key;
      if(valid.length>1)map.current.fitBounds(L.latLngBounds(valid.map(p=>[p.latitude,p.longitude])),{padding:[25,25],maxZoom:zoom,animate:false});
      else if(valid.length===1)map.current.setView([valid[0].latitude,valid[0].longitude],zoom,{animate:false});
      else if(center&&Number.isFinite(center.latitude)&&Number.isFinite(center.longitude))map.current.setView([center.latitude,center.longitude],zoom,{animate:false});
      else map.current.fitBounds([[-60,-180],[80,180]],{animate:false});
    }
    return()=>{if(map.current)map.current.removeLayer(group);};
  },[ready,valid,center?.latitude,center?.longitude,zoom,onSelect]);
  return <div className="mundely-place-results-map" aria-label={label}>
    <div ref={node} style={{height:260,minHeight:200,width:'100%'}}/>
    {error&&<p role="status">{label}: {language==="it"?"Mappa non disponibile":language==="es"?"Mapa no disponible":"Map unavailable"}</p>}
    {showResultButtons&&valid.length>0&&<div className="mundely-poi-map-buttons">{valid.map(point=><button key={point.id} type="button" onClick={()=>onSelect(point.id)}>{point.number??'•'} · {point.name}</button>)}</div>}
  </div>;
}
